import React from "react";
import { useAuthClaims } from "@redriver/cinnamon";
import { withRouter } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getAgreementsState } from "../../selectors";
import {
  PermissionCheck,
  withCurrentUser,
  withPermissions,
} from "features/../../../shared/components/auth";
import { guidsAreEqual } from "features/../../../shared/modules/helpers";
import { Targets, Actions } from "constants/permissions";
import { AppRoutes } from "constants/routes";
import {
  PhysicalDocumentSheetStatePrincipal,
  SheetActionIps,
  SheetActionPrincipal,
  SheetStateIps,
  SheetStatePrincipal,
} from "features/../../../shared/constants/enums";

import {
  getMasterAgreement,
  getMasterAgreements,
  addExpandedAgreementOrSchedule,
  getAgreementScheduleSheets,
} from "../actions";
import {
  SubmitSheet,
  SetPhysicalDocumentSheetState,
  RenameSheet,
  SheetStateProgressView,
  SendForApproval,
  PreApproveSheetPrincipal,
  PreApproveSheetIps,
  MakeSheetLive,
  ResetSheetState,
  AddAdminComment,
  AmendExpiryDate,
  DeclinePreApprovalPrincipal,
  DeclinePreApprovalIps,
  DocumentRecipientsView,
} from "../SheetActions";
import {
  SetSheetOwner,
  CloneSheet,
  DeleteSheet,
  CrystalliseSheet,
  UncrystalliseSheet,
  ArchiveSheet,
  UnarchiveSheet,
  ToggleSheetReadOnly,
  DuplicateToNewMaster,
} from "features/Sheets/ManageSheet/SheetActions";
import { SetCurrentSchedule } from "../SetCurrentSchedule";
import { SetReplacingSchedule } from "../SetReplacingSchedule";
import { VoidEnvelope } from "../SheetActions/VoidEnvelope";
import SendToFunder from "../SheetActions/SendToFunder/SendToFunder";
import { RejectDeal } from "../SheetActions/RejectDeal";
import { useTranslation } from "react-i18next";
import ToggleSheetRewriteOnly from "features/Sheets/ManageSheet/SheetActions/ToggleSheetRewriteOnly";
import { Dropdown, DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";
import MoveSheetModal from "../SheetActions/MoveSheet/MoveSheetModal";

const SheetActions = ({
  agreement,
  schedule,
  sheet,
  currentUser,
  hasPermission,
  history,
  onClientSelect,
}) => {
  const { t } = useTranslation("agreements", { keyPrefix: "SheetActions" });
  const dispatch = useDispatch();
  const { filters, companyFilters, pagination } =
    useSelector(getAgreementsState);

  const dealerId = companyFilters?.dealer?.id;
  let { sheetId, sheetName, ownedBy, isReadOnly, isRewriteOnly } = sheet,
    { agreementReference, boilerPlateTandCsId } = agreement,
    { scheduleReference } = schedule;
  const { isSupplierAdminUser, isInternalUser } = currentUser;
  const isAdminUser = isInternalUser || isSupplierAdminUser;

  const canPreApproveAsSupplier =
    hasPermission(Targets.Sheet, Actions.PreApproveSupplier) &&
    (process.env.TRADE_AS_IPS
      ? sheet.stateIps == SheetStateIps.PreLive_AwaitingSupplierPreApproval
      : sheet.statePrincipal ==
          SheetStatePrincipal.PreLive_AwaitingPrincipalPreApproval &&
        !sheet.submittedForPhysicalDocumentFlow);

  const canPreApproveAsIPS =
    process.env.TRADE_AS_IPS &&
    isInternalUser &&
    hasPermission(Targets.Sheet, Actions.PreApproveIPS) &&
    sheet.stateIps == SheetStateIps.PreLive_AwaitingIpsPreApproval;
  const sheetInPreApprovalState = process.env.TRADE_AS_IPS
    ? sheet.stateIps == SheetStateIps.PreLive_AwaitingIpsPreApproval ||
      sheet.stateIps == SheetStateIps.PreLive_AwaitingSupplierPreApproval
    : sheet.statePrincipal ==
      SheetStatePrincipal.PreLive_AwaitingPrincipalPreApproval;

  const { userId } = useAuthClaims();
  const canDeleteAll = hasPermission(Targets.Sheet, Actions.DeleteAll);
  const canDeleteVoided = hasPermission(Targets.Sheet, Actions.DeleteVoided);

  const userCanDelete =
    canDeleteAll ||
    (sheet.isDraft && guidsAreEqual(sheet.ownedById, userId)) ||
    (canDeleteVoided && (sheet.isVoided || sheet.isDraft));

  const showDeleteSheet =
    userCanDelete && (!isReadOnly || isAdminUser) && !sheet.isDeclined;

  const reloadAgreement = () =>
    dispatch(getMasterAgreement(agreement.agreementId, filters));

  const reloadAgreements = () =>
    dispatch(getMasterAgreements({ filters, pagination }, companyFilters));

  const onDuplicateToNewMasterSubmitted = (response) => {
    reloadAgreements();
    dispatch(getAgreementScheduleSheets(response.scheduleId, filters));

    // Expand newly created agreement and schedule, so using the response IDs.
    dispatch(addExpandedAgreementOrSchedule(response.masterAgreementId));
    dispatch(addExpandedAgreementOrSchedule(response.scheduleId));
  };

  const reloadSheetsForSchedule = () =>
    dispatch(getAgreementScheduleSheets(schedule.scheduleId, filters));

  return (
    <Dropdown
      icon
      trigger={<Icon name="ellipsis horizontal" />}
      vertical
      pointing="right"
    >
      <DropdownMenu>
        {!sheet.isReadOnly && (
          <Dropdown
            item
            icon
            pointing="right"
            trigger={
              <div className="flex">
                <Icon name="caret left" />
                {t("Groups.New_Sheet_Actions")}
              </div>
            }
          >
            <DropdownMenu>
              {!sheet.isDeclined && (
                <CloneSheet
                  sheetId={sheetId}
                  name={sheetName}
                  isLive={sheet.isLive}
                />
              )}
              {!sheet.isDeclined && (
                <PermissionCheck
                  action={Actions.DuplicateToNewMaster}
                  target={Targets.Sheet}
                >
                  <DuplicateToNewMaster
                    sheetId={sheetId}
                    name={sheetName}
                    onSubmitted={onDuplicateToNewMasterSubmitted}
                  />
                </PermissionCheck>
              )}
            </DropdownMenu>
          </Dropdown>
        )}
        {!sheet.isReadOnly && (
          <Dropdown
            item
            icon
            pointing="right"
            trigger={
              <div className="flex">
                <Icon name="caret left" />
                {t("Groups.Sheet_Actions")}
              </div>
            }
          >
            <DropdownMenu>
              {(!isReadOnly || isAdminUser) && !sheet.isDeclined && (
                <RenameSheet
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}
              {showDeleteSheet && (
                <DeleteSheet
                  sheet={sheet}
                  onSubmitted={() => {
                    reloadSheetsForSchedule();
                    reloadAgreement();
                  }}
                  submitParams={{ sheetId }}
                />
              )}
              {isAdminUser && !sheet.isDeclined && (
                <AmendExpiryDate
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}
              {isAdminUser && !sheet.isDeclined && (
                <PermissionCheck
                  target={Targets.Sheet}
                  action={Actions.UpdateSheetScheduleReference}
                >
                  <SetCurrentSchedule
                    sheet={sheet}
                    onSubmitted={reloadSheetsForSchedule}
                  />
                </PermissionCheck>
              )}
              {isInternalUser && !sheet.isDeclined && (
                <SetReplacingSchedule
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}
            </DropdownMenu>
          </Dropdown>
        )}
        <Dropdown
          item
          icon
          pointing="right"
          trigger={
            <div className="flex">
              <Icon name="caret left" />
              {t("Groups.Document_Actions")}
            </div>
          }
        >
          <DropdownMenu>
            {!sheetInPreApprovalState &&
              !sheet.isDeclined &&
              !sheet.submittedForPhysicalDocumentFlow && (
                <PermissionCheck
                  target={Targets.Sheet}
                  action={Actions.SubmitForCustomerApproval}
                >
                  <SendForApproval
                    sheet={sheet}
                    onSubmitted={() => {
                      reloadSheetsForSchedule();
                      reloadAgreement();
                    }}
                    scheduleReference={scheduleReference}
                    agreementReference={agreementReference}
                    boilerPlateTandCsId={boilerPlateTandCsId}
                    sheetActionRequired={
                      process.env.TRADE_AS_IPS
                        ? SheetActionIps.CanSendForApproval
                        : SheetActionPrincipal.CanSendForApproval
                    }
                  />
                </PermissionCheck>
              )}

            {!sheet.submittedForPhysicalDocumentFlow &&
              !sheet.submittedForDocusignFlow &&
              (!isReadOnly || isAdminUser) && (
                <PermissionCheck target={Targets.Sheet} action={Actions.Submit}>
                  <SubmitSheet
                    sheet={sheet}
                    onSubmitted={reloadSheetsForSchedule}
                    boilerPlateTandCsId={boilerPlateTandCsId}
                  />
                </PermissionCheck>
              )}
            {sheet.submittedForDocusignFlow && (
              <PermissionCheck
                target={Targets.Sheet}
                action={Actions.ViewDocumentRecipients}
              >
                <DocumentRecipientsView sheetId={sheetId} />
              </PermissionCheck>
            )}

            {process.env.TRADE_AS_IPS &&
              !sheet.isDeclined &&
              (canPreApproveAsSupplier || canPreApproveAsIPS) && (
                <PreApproveSheetIps
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}
            {!process.env.TRADE_AS_IPS &&
              canPreApproveAsSupplier &&
              !sheet.isDeclined && (
                <PreApproveSheetPrincipal
                  sheet={sheet}
                  onSubmitted={() => {
                    reloadSheetsForSchedule();
                    reloadAgreement();
                  }}
                  scheduleReference={scheduleReference}
                  agreementReference={agreementReference}
                  boilerPlateTandCsId={boilerPlateTandCsId}
                  sheetActionRequired={
                    SheetActionPrincipal.CanBePreApprovedByPrincipal
                  }
                />
              )}

            {process.env.TRADE_AS_IPS &&
              !sheet.isDeclined &&
              (canPreApproveAsSupplier || canPreApproveAsIPS) && (
                <DeclinePreApprovalIps
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}

            {!process.env.TRADE_AS_IPS &&
              !sheet.isDeclined &&
              canPreApproveAsSupplier && (
                <DeclinePreApprovalPrincipal
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              )}
            {sheet.isInDocuSign && (
              <PermissionCheck action={Actions.Void} target={Targets.Sheet}>
                <VoidEnvelope
                  sheet={sheet}
                  onSubmitted={() => {
                    reloadSheetsForSchedule();
                    reloadAgreement();
                  }}
                />
              </PermissionCheck>
            )}
            {isAdminUser && (
              <PermissionCheck action={Actions.Reset} target={Targets.Sheet}>
                <ResetSheetState
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                  sheetActionRequired={
                    process.env.TRADE_AS_IPS
                      ? SheetActionIps.CanResetSheetState
                      : SheetActionPrincipal.CanResetSheetState
                  }
                />
              </PermissionCheck>
            )}
            {!sheet.submittedForPhysicalDocumentFlow && !sheet.isDeclined && (
              <SheetStateProgressView sheet={sheet} />
            )}
            {!sheet.isDeclined && (
              <React.Fragment>
                {!process.env.TRADE_AS_IPS &&
                  isAdminUser &&
                  sheet.submittedForPhysicalDocumentFlow &&
                  !sheet.submittedForDocusignFlow &&
                  sheet.physicalDocumentSheetStatePrincipal ===
                    PhysicalDocumentSheetStatePrincipal.SubmittedToPrincipalAdmin && (
                    <PermissionCheck
                      target={Targets.Sheet}
                      action={Actions.SetPhysicalDocumentSheetState}
                    >
                      <SendToFunder
                        sheet={sheet}
                        onSubmitted={reloadSheetsForSchedule}
                      />
                    </PermissionCheck>
                  )}
                {isAdminUser && (
                  <PermissionCheck
                    target={Targets.Sheet}
                    action={Actions.RejectDeal}
                  >
                    <RejectDeal
                      sheet={sheet}
                      onSubmitted={reloadSheetsForSchedule}
                    />
                  </PermissionCheck>
                )}
              </React.Fragment>
            )}
          </DropdownMenu>
        </Dropdown>
        <Dropdown
          item
          icon
          pointing="right"
          trigger={
            <div className="flex">
              <Icon name="caret left" />
              {t("Groups.Admin_Actions")}
            </div>
          }
        >
          <DropdownMenu>
            <DropdownItem
              onClick={() =>
                history.push(`${AppRoutes.Notification}?sheetId=${sheetId}`)
              }
            >
              {t("ViewNotifications")}
            </DropdownItem>
            {!isReadOnly && !sheet.isDeclined && (
              <PermissionCheck target={Targets.Sheet} action={Actions.SetOwner}>
                <SetSheetOwner
                  sheetId={sheetId}
                  dealerId={dealerId}
                  ownedBy={ownedBy}
                  onSubmitted={reloadSheetsForSchedule}
                />
              </PermissionCheck>
            )}
            {isAdminUser && !sheet.isDeclined && !isReadOnly && (
              <AddAdminComment
                sheet={sheet}
                onSubmitted={reloadSheetsForSchedule}
              />
            )}
            {!sheet.submittedForDocusignFlow && !isReadOnly && (
              <PermissionCheck
                target={Targets.Sheet}
                action={Actions.SetPhysicalDocumentSheetState}
              >
                <SetPhysicalDocumentSheetState
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              </PermissionCheck>
            )}
            {!isReadOnly && !sheet.isDeclined && (
              <PermissionCheck
                target={Targets.Sheet}
                action={Actions.CrystalliseAndUncrystalliseSheets}
              >
                <UncrystalliseSheet
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                  sheetActionRequired={
                    process.env.TRADE_AS_IPS
                      ? SheetActionIps.CanUncrystallise
                      : SheetActionPrincipal.CanUncrystallise
                  }
                />
                <CrystalliseSheet
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                  sheetActionRequired={
                    process.env.TRADE_AS_IPS
                      ? SheetActionIps.CanCrystallise
                      : SheetActionPrincipal.CanCrystallise
                  }
                />
              </PermissionCheck>
            )}
            {!isReadOnly && !sheet.isDeclined && sheet.isArchived && (
              <UnarchiveSheet
                sheet={sheet}
                onSubmitted={reloadSheetsForSchedule}
              />
            )}
            {!isReadOnly && !sheet.isDeclined && !sheet.isArchived && (
              <ArchiveSheet
                sheet={sheet}
                onSubmitted={reloadSheetsForSchedule}
              />
            )}
            {(!isReadOnly || isAdminUser) && !sheet.isDeclined && (
              <PermissionCheck
                target={Targets.Sheet}
                action={Actions.SetReadOnly}
              >
                <ToggleSheetReadOnly
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              </PermissionCheck>
            )}
            {isAdminUser && !sheet.isDeclined && !isReadOnly && (
              <PermissionCheck
                target={Targets.Sheet}
                action={Actions.SetRewriteOnly}
              >
                <ToggleSheetRewriteOnly
                  sheet={sheet}
                  onSubmitted={reloadSheetsForSchedule}
                />
              </PermissionCheck>
            )}
            {!sheet.isDeclined && !isReadOnly && (
              <MakeSheetLive
                sheet={sheet}
                scheduleIsLive={schedule.isLive}
                sheetActionRequired={
                  process.env.TRADE_AS_IPS
                    ? SheetActionIps.CanMakeLive
                    : SheetActionPrincipal.CanMakeLive
                }
                onSubmitted={() => {
                  reloadSheetsForSchedule();
                  reloadAgreement();
                }}
                scheduleReference={scheduleReference}
                agreementReference={agreementReference}
              />
            )}
            {isAdminUser && (
              <MoveSheetModal
                sheet={sheet}
                reloadSheetsForSchedule={reloadSheetsForSchedule}
                onSubmitted={reloadSheetsForSchedule}
                onClientSelect={onClientSelect}
              />
            )}
          </DropdownMenu>
        </Dropdown>
      </DropdownMenu>
    </Dropdown>
  );
};

export default withPermissions(withCurrentUser(withRouter(SheetActions)));
